import { useMemo, type ReactNode } from 'react'
import { Package } from 'lucide-react'
import { useDeliveredQuoteItemsForHistory } from 'src/api/db/apiQuoteItem'
import { PageContainer } from 'src/shared/components/PageContainer'
import { EmptyState } from 'src/shared/components/EmptyState'
import { FullScreenLoader } from 'src/shared/components/FullScreenLoader'
import { ProductGrid } from 'src/shared/components/ProductGrid'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'

type DeliveredOrdersProductGridProps = {
  embedded?: boolean
}

export const DeliveredOrdersProductGrid = (
  props: DeliveredOrdersProductGridProps = {}
) => {
  const { embedded = false } = props
  const { t } = useLocalization()
  const isAdmin = useIsAdmin()
  const { data: rows, isPending } = useDeliveredQuoteItemsForHistory()

  const product_items = useMemo(() => {
    const list = rows ?? []
    const seen = new Set<number>()
    const out: { product_id?: number; product_variant_color_id?: number }[] = []

    for (const row of list) {
      const pid = row.product_id
      if (pid == null) continue
      if (seen.has(pid)) continue
      seen.add(pid)
      out.push({
        product_id: pid,
        product_variant_color_id: row.product_variant_color_id ?? undefined,
      })
    }

    return out
  }, [rows])

  const shell = (children: ReactNode) =>
    embedded ? (
      <Column className="gap-4">{children}</Column>
    ) : (
      <PageContainer className="gap-4">{children}</PageContainer>
    )

  if (isPending) {
    return <FullScreenLoader />
  }

  if (product_items.length === 0) {
    return shell(
      <Column className="gap-4">
        {!embedded && (
          <Row className="items-center gap-2">
            <Package className="h-5 w-5" />
            <h1>{t('Items', 'Artiklar')}</h1>
          </Row>
        )}

        <EmptyState
          icon={<Package className="w-9 h-9" />}
          title={t('No items yet', 'Inga artiklar ännu')}
          description={t(
            'Delivered orders will list products here',
            'Levererade ordrar listar produkter här'
          )}
        />
      </Column>
    )
  }

  return shell(
    <Column>
      {!embedded && (
        <>
          <Row className="items-center gap-2">
            <Package className="h-5 w-5" />
            <h1>{t('Items', 'Artiklar')}</h1>
          </Row>

          <p className="text-sm md:text-base text-gray-600">
            {t(
              'Products from your delivered orders',
              'Produkter från dina levererade ordrar'
            )}
          </p>
        </>
      )}

      <Column className="mt-4">
        <ProductGrid
          product_items={product_items}
          scrollArea={false}
          showAddToQuote={true}
          showReorder={!isAdmin}
        />
      </Column>

      <div className="h-10" />
    </Column>
  )
}
